import { Box, Checkbox, FormControlLabel, MenuItem, TextField, Typography } from '@mui/material'
import type { Distributor } from '../../api/types'

/** Co se má kromě vygenerování dokumentů udělat (volí se u každé smlouvy). */
export interface GenerationOptionsState {
  /** Založit klienta (a vklad do fronty plateb) v aplikaci. */
  createClient: boolean
  /** Založit přihlašovací údaje a poslat uvítací e-mail (jen se založeným klientem). */
  sendCredentials: boolean
  /** Nový klient: rovnou přiřadit distributora ('' = bez distributora). */
  distributorId: string
}

export const DEFAULT_GENERATION_OPTIONS: GenerationOptionsState = {
  createClient: true,
  sendCredentials: false,
  distributorId: '',
}

interface GenerationOptionsProps {
  options: GenerationOptionsState
  onChange: (options: GenerationOptionsState) => void
  /** Nový klient — jen u něj má smysl zakládat klienta a přihlašovací údaje. */
  isNewClient: boolean
  /** Distributoři s jmennou rolí, ze kterých jde vybrat obsluhu nového klienta. */
  distributors: Distributor[]
  /** Hlavní vypínač e-mailů je vypnutý — e-maily neodejdou, i když jsou zaškrtnuté. */
  emailsDisabled?: boolean
  disabled?: boolean
}

/**
 * Zaškrtávátka těsně před tlačítkem „Vygenerovat smlouvy“ (Michal 14. 10.: místo karty
 * Automatické akce nahoře). Pořadí odpovídá závislosti: bez založeného klienta nejde
 * založit přístup, a kdo dostane přístup, dostane i uvítací e-mail.
 */
export function GenerationOptions({
  options,
  onChange,
  isNewClient,
  distributors,
  emailsDisabled,
  disabled,
}: GenerationOptionsProps) {
  const set = (patch: Partial<GenerationOptionsState>) => {
    const next = { ...options, ...patch }
    if (!next.createClient) {
      next.sendCredentials = false
      next.distributorId = ''
    }
    onChange(next)
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        border: 1,
        borderColor: 'divider',
        borderRadius: 2,
        px: 2,
        py: 1,
        bgcolor: 'background.paper',
      }}
    >
      <Typography variant="subtitle2" sx={{ mt: 0.5 }}>
        Po vygenerování
      </Typography>
      {isNewClient && (
        <FormControlLabel
          control={
            <Checkbox
              checked={options.createClient}
              onChange={e => set({ createClient: e.target.checked })}
              disabled={disabled}
            />
          }
          label="Založit klienta v aplikaci (a vklad do fronty plateb)"
        />
      )}
      {isNewClient && (
        <FormControlLabel
          sx={{ pl: 3 }}
          control={
            <Checkbox
              checked={options.sendCredentials}
              onChange={e => set({ sendCredentials: e.target.checked })}
              disabled={disabled || !options.createClient}
            />
          }
          label="Založit přihlašovací údaje a poslat uvítací e-mail"
        />
      )}
      {isNewClient && (
        <TextField
          select
          size="small"
          label="Distributor klienta"
          value={options.distributorId}
          onChange={e => set({ distributorId: e.target.value })}
          disabled={disabled || !options.createClient}
          helperText="E-maily klientovi dostane v kopii, pokud má v záložce Distributoři zapnuté informování."
          sx={{ mt: 1, mb: 0.5, maxWidth: 420 }}
        >
          <MenuItem value="">
            <em>bez distributora</em>
          </MenuItem>
          {distributors.map(d => (
            <MenuItem key={d.id} value={d.id}>
              {d.name}
            </MenuItem>
          ))}
        </TextField>
      )}
      {emailsDisabled && (
        <Typography variant="caption" color="warning.main" sx={{ mb: 0.5 }}>
          E-maily jsou v nastavení modulu vypnuté, žádný e-mail neodejde.
        </Typography>
      )}
    </Box>
  )
}
